import React, { useState, useEffect, useRef } from 'react';
import CytoscapeComponent from 'react-cytoscapejs';
import { Drawer, Button, Spin, Select } from 'antd';
import './ImpactAnalysis.css';
import {
  shiftBatModelData,
  routeModelData,
  stopModelData,
  rosterModelData,
  jobModelData,
  bookingModelData,
  studentModelData,
  charterModelData,
  scheduleModelData
} from '../services/ModelService';
import { useAppContext } from '../libs/contextLib';

// Node color by type
const nodeColor = {
  shiftbat: '#1890ff',
  route: '#52c41a',
  stop: '#fa8c16',
  roster: '#722ed1',
  job: '#13c2c2',
  booking: '#eb2f96',
  student: '#bfbfbf',
  charter: '#fadb14', // yellow for charters
  calendar: '#ff85c0', // pink for schedules/calendars
};

// Helper to safely stringify objects with cycles
function safeStringify(obj, space = 2) {
  const seen = new WeakSet();
  return JSON.stringify(obj, function(key, value) {
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) return '[Circular]';
      seen.add(value);
    }
    return value;
  }, space);
}

const COSE_LAYOUT = {
  name: 'cose',
  animate: true,
  randomize: true,
  nodeRepulsion: 12000,
  idealEdgeLength: 200,
};

export default function ImpactAnalysis() {
  const { setBreadcrumb, setPageTitle } = useAppContext();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [elements, setElements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allNodes, setAllNodes] = useState([]); // all nodes for search
  const [allEdges, setAllEdges] = useState([]); // all edges for filtering
  const [searchOptions, setSearchOptions] = useState([]); // for Select
  const [selectedKeys, setSelectedKeys] = useState([]); // selected node ids
  const [filteredElements, setFilteredElements] = useState([]); // filtered for graph
  const [selectedType, setSelectedType] = useState('shiftbat'); // default type
  const [depth, setDepth] = useState(1); // default depth
  const cyRef = useRef(null);
  const [isPrettifying, setIsPrettifying] = useState(false);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      const [shiftbats, routes, stops, rosters, jobs, bookings, students, charters, schedules] = await Promise.all([
        shiftBatModelData.getAll(),
        routeModelData.getAll(),
        stopModelData.getAll(),
        rosterModelData.getAll(),
        jobModelData.getAll(),
        bookingModelData.getAll(),
        studentModelData.getAll(),
        charterModelData.getAll(),
        scheduleModelData.getAll(),
      ]);
      // Build nodes
      const nodes = [];
      const options = [];
      Object.values(shiftbats).forEach(sb => {
        nodes.push({ data: { id: sb.shiftBatId, label: sb.shiftBatName || sb.shiftBatNumber || sb.shiftBatId, type: 'shiftbat', color: nodeColor.shiftbat, raw: sb } });
        options.push({ value: sb.shiftBatId, label: `[ShiftBat] ${sb.shiftBatName || sb.shiftBatNumber || sb.shiftBatId}`, type: 'shiftbat' });
      });
      Object.values(routes).forEach(r => {
        nodes.push({ data: { id: r.routeId, label: r.routeName || r.routeNumber || r.routeId, type: 'route', color: nodeColor.route, raw: r } });
        options.push({ value: r.routeId, label: `[Route] ${r.routeName || r.routeNumber || r.routeId}`, type: 'route' });
      });
      Object.values(stops).forEach(s => {
        nodes.push({ data: { id: s.stopId, label: s.stopName || s.stopId, type: 'stop', color: nodeColor.stop, raw: s } });
        options.push({ value: s.stopId, label: `[Stop] ${s.stopName || s.stopId}`, type: 'stop' });
      });
      Object.values(rosters).forEach(r => {
        nodes.push({ data: { id: r.rosterId, label: r.rosterName || r.rosterId, type: 'roster', color: nodeColor.roster, raw: r } });
        options.push({ value: r.rosterId, label: `[Roster] ${r.rosterName || r.rosterId}`, type: 'roster' });
      });
      Object.values(jobs).forEach(j => {
        nodes.push({ data: { id: j.jobId, label: j.name || j.jobId, type: 'job', color: nodeColor.job, raw: j } });
        options.push({ value: j.jobId, label: `[Job] ${j.name || j.jobId}`, type: 'job' });
      });
      Object.values(bookings).forEach(b => {
        nodes.push({ data: { id: b.bookingId, label: b.bookingReference || b.bookingId, type: 'booking', color: nodeColor.booking, raw: b } });
        options.push({ value: b.bookingId, label: `[Booking] ${b.bookingReference || b.bookingId}`, type: 'booking' });
      });
      Object.values(students).forEach(s => {
        nodes.push({ data: { id: s.studentId, label: s.name ? s.name() : s.studentId, type: 'student', color: nodeColor.student, raw: s } });
        options.push({ value: s.studentId, label: `[Student] ${s.name ? s.name() : s.studentId}`, type: 'student' });
      });
      Object.values(charters).forEach(c => {
        nodes.push({ data: { id: c.charterId, label: c.name || c.charterId, type: 'charter', color: nodeColor.charter, raw: c } });
        options.push({ value: c.charterId, label: `[Charter] ${c.name || c.charterId}`, type: 'charter' });
      });
      // Add schedules/calendars
      Object.values(schedules).forEach(sch => {
        nodes.push({ data: { id: sch.scheduleId, label: sch.scheduleName || sch.scheduleId, type: 'calendar', color: nodeColor.calendar, raw: sch } });
        options.push({ value: sch.scheduleId, label: `[Schedule] ${sch.scheduleName || sch.scheduleId}`, type: 'calendar' });
      });
      // Build edges (same as before)
      const edges = [];
      Object.values(shiftbats).forEach(sb => {
        if (Array.isArray(sb.routeIds)) {
          sb.routeIds.forEach(rid => edges.push({ data: { source: sb.shiftBatId, target: rid, label: 'part of' } }));
        }
        if (Array.isArray(sb.rows)) {
          sb.rows.forEach(row => {
            const type = (row.type || '').toLowerCase();
            if (type === 'service' || type === 'Service' || type === 'Service'.toLowerCase()) {
              if (row.routeId) {
                edges.push({ data: { source: sb.shiftBatId, target: row.routeId, label: 'route' } });
              }
              if (row.tripId) {
                edges.push({ data: { source: sb.shiftBatId, target: row.tripId, label: 'trip' } });
              }
              if (Array.isArray(row.stopRows)) {
                row.stopRows.forEach(stopRow => {
                  if (stopRow.stopId) {
                    edges.push({ data: { source: sb.shiftBatId, target: stopRow.stopId, label: 'stop' } });
                  }
                });
              }
            }
          });
        }
        // Connect shiftbat to its schedules/calendars
        if (Array.isArray(sb.scheduleIds)) {
          sb.scheduleIds.forEach(sid => edges.push({ data: { source: sb.shiftBatId, target: sid, label: 'calendar' } }));
        }
      });
      Object.values(routes).forEach(r => {
        if (Array.isArray(r.stops)) {
          r.stops.forEach(s => edges.push({ data: { source: r.routeId, target: s.stopId, label: 'uses' } }));
        }
        // Connect route to its schedule/calendar
        if (r.scheduleId) {
          edges.push({ data: { source: r.routeId, target: r.scheduleId, label: 'calendar' } });
        }
      });
      Object.values(rosters).forEach(roster => {
        if (Array.isArray(roster.scenarios)) {
          roster.scenarios.forEach(scenario => {
            if (Array.isArray(scenario.workItems)) {
              scenario.workItems.forEach(workItemsForDay => {
                (workItemsForDay || []).forEach(workItem => {
                  if (workItem.duty === undefined) return;
                  const sbId = typeof workItem.duty === 'string' ? workItem.duty : workItem.duty?.shiftBatId;
                  if (sbId) edges.push({ data: { source: sbId, target: roster.rosterId, label: 'in roster' } });
                });
              });
            }
          });
        }
      });
      Object.values(jobs).forEach(job => {
        if (job.type === 'shiftbat' && job.typeId) {
          edges.push({ data: { source: job.typeId, target: job.jobId, label: 'job for' } });
        }
      });
      Object.values(bookings).forEach(booking => {
        if (Array.isArray(booking.jobs)) {
          booking.jobs.forEach(jid => edges.push({ data: { source: jid, target: booking.bookingId, label: 'for booking' } }));
        }
      });
      Object.values(bookings).forEach(booking => {
        if (Array.isArray(booking.students)) {
          booking.students.forEach(sid => edges.push({ data: { source: booking.bookingId, target: sid, label: 'for student' } }));
        }
      });
      Object.values(students).forEach(student => {
        if (Array.isArray(student.bookings)) {
          student.bookings.forEach(bid => edges.push({ data: { source: student.studentId, target: bid, label: 'has booking' } }));
        }
      });
      Object.values(charters).forEach(c => {
        if (Array.isArray(c.duties)) {
          c.duties.forEach(duty => {
            const sbId = typeof duty === 'string' ? duty : duty?.shiftBatId;
            if (sbId) edges.push({ data: { source: c.charterId, target: sbId, label: 'duty' } });
          });
        }
        if (Array.isArray(c.itinerary)) {
          c.itinerary.forEach(route => {
            const routeId = typeof route === 'string' ? route : route?.routeId;
            if (routeId) edges.push({ data: { source: c.charterId, target: routeId, label: 'itinerary' } });
          });
        }
        if (c.customerId) {
          edges.push({ data: { source: c.charterId, target: c.customerId, label: 'customer' } });
        }
      });
      // Optionally, connect schedules to routes/shiftbats/charters if they reference scheduleId
      setAllNodes(nodes);
      setAllEdges(edges);
      setSearchOptions(options);
      setElements([...nodes, ...edges]);
      setLoading(false);
    }
    fetchData();
  }, []);

  // Depth options
  const depthOptions = [
    { value: 1, label: 'Depth: 1' },
    { value: 2, label: 'Depth: 2' },
    { value: 3, label: 'Depth: 3' },
    { value: 4, label: 'Depth: 4' },
    { value: 5, label: 'Depth: 5' },
  ];

  // Filter graph based on selectedKeys
  useEffect(() => {
    if (!selectedKeys.length) {
      setFilteredElements([]); // nothing selected
      return;
    }
    // Traverse the graph up to the selected depth
    const nodeSet = new Set(selectedKeys);
    const edgeSet = new Set();
    let currentLevel = new Set(selectedKeys);
    for (let d = 0; d < depth; d++) {
      const nextLevel = new Set();
      allEdges.forEach(edge => {
        const { source, target } = edge.data;
        if (currentLevel.has(source) || currentLevel.has(target)) {
          edgeSet.add(edge);
          if (!nodeSet.has(source)) nextLevel.add(source);
          if (!nodeSet.has(target)) nextLevel.add(target);
          nodeSet.add(source);
          nodeSet.add(target);
        }
      });
      currentLevel = nextLevel;
      if (currentLevel.size === 0) break;
    }
    // Build filtered nodes
    const filteredNodes = allNodes.filter(n => nodeSet.has(n.data.id));
    const filteredNodeIds = new Set(filteredNodes.map(n => n.data.id));
    // Only include edges where both source and target are present
    const filteredEdges = Array.from(edgeSet).filter(edge =>
      filteredNodeIds.has(edge.data.source) && filteredNodeIds.has(edge.data.target)
    );
    setFilteredElements([...filteredNodes, ...filteredEdges]);
    // Auto-prettify layout after updating elements
    setTimeout(() => {
      if (
        cyRef.current &&
        cyRef.current.nodes().length > 0 &&
        cyRef.current.container()
      ) {
        setIsPrettifying(true);
        const layout = cyRef.current.layout(COSE_LAYOUT);
        layout.run();
        cyRef.current.one('layoutstop', () => setIsPrettifying(false));
      }
    }, 0);
  }, [selectedKeys, allNodes, allEdges, depth]);

  // Style nodes by type
  const style = [
    {
      selector: 'node',
      style: {
        'background-color': 'data(color)',
        label: 'data(label)',
        'text-valign': 'center',
        'text-halign': 'center',
        'font-size': 14,
        'color': '#222',
        'width': 60,
        'height': 60,
        'border-width': 2,
        'border-color': '#fff',
        'text-outline-width': 1,
        'text-outline-color': '#fff',
      },
    },
    {
      selector: 'edge',
      style: {
        width: 2,
        'line-color': '#aaa',
        'target-arrow-color': '#aaa',
        'target-arrow-shape': 'triangle',
        'curve-style': 'bezier',
        label: 'data(label)',
        'font-size': 12,
        'text-background-color': '#fff',
        'text-background-opacity': 1,
        'text-background-padding': 2,
      },
    },
  ];

  // Build type options for the first dropdown
  const typeOptions = [
    { value: 'shiftbat', label: 'ShiftBats' },
    { value: 'route', label: 'Routes/Services' },
    { value: 'charter', label: 'Charters' },
    { value: 'stop', label: 'Stops' },
    { value: 'student', label: 'People' },
    { value: 'booking', label: 'Bookings' },
    { value: 'roster', label: 'Rosters' },
    { value: 'job', label: 'Jobs' },
    { value: 'calendar', label: 'Calendars' },
  ];

  // Filter search options by selected type
  const filteredSearchOptions = searchOptions.filter(opt => opt.type === selectedType);

  useEffect(() => {
    setBreadcrumb("Impact Analysis");
    setPageTitle("Impact Analysis");
  }, [setBreadcrumb, setPageTitle]);

  return (
    <div className="ImpactAnalysisContainer">
      <div className="ImpactAnalysisSearchBar">
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Select
            style={{ width: 180 }}
            value={selectedType}
            onChange={setSelectedType}
            options={typeOptions}
          />
          <Select
            mode="multiple"
            allowClear
            showSearch
            style={{ width: '100%' }}
            placeholder={`Search and select ${typeOptions.find(t => t.value === selectedType)?.label || ''}...`}
            options={filteredSearchOptions}
            value={selectedKeys}
            onChange={setSelectedKeys}
            optionFilterProp="label"
            filterOption={(input, option) =>
              option.label.toLowerCase().includes(input.toLowerCase())
            }
          />
          <Select
            style={{ width: 120 }}
            value={depth}
            onChange={setDepth}
            options={depthOptions}
          />
          <Button onClick={() => setSelectedKeys([])} disabled={!selectedKeys.length}>
            Clear Selection
          </Button>
          <Button
            onClick={() => {
              if (
                cyRef.current &&
                cyRef.current.nodes().length > 0 &&
                cyRef.current.container()
              ) {
                setIsPrettifying(true);
                const layout = cyRef.current.layout(COSE_LAYOUT);
                layout.run();
                cyRef.current.one('layoutstop', () => setIsPrettifying(false));
              }
            }}
            disabled={!filteredElements.length}
          >
            Prettify Layout
          </Button>
        </div>
      </div>
      <div className="ImpactAnalysisGraph">
        {loading ? (
          <Spin size="large" style={{ position: 'absolute', top: '50%', left: '50%' }} />
        ) : (
          <>
            <CytoscapeComponent
              elements={selectedKeys.length ? filteredElements : []}
              style={{ width: '100%', height: '70vh', background: '#f0f2f5', borderRadius: 12, boxShadow: '0 2px 12px #0001' }}
              layout={COSE_LAYOUT}
              stylesheet={style}
              cy={cy => {
                cyRef.current = cy;
                cy.on('tap', 'node', evt => {
                  const node = evt.target;
                  setSelectedNode(node.data());
                  setDrawerOpen(true);
                });
              }}
            />
            {isPrettifying && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: 'rgba(255,255,255,0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10
              }}>
                <Spin size="large" tip="Prettifying layout..." />
              </div>
            )}
          </>
        )}
      </div>
      <Drawer
        title={selectedNode ? selectedNode.label : ''}
        placement="right"
        width={400}
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
      >
        {selectedNode && (
          <pre style={{ fontSize: 13, background: '#fafafa', padding: 12, borderRadius: 4 }}>
            {safeStringify(selectedNode.raw)}
          </pre>
        )}
        {/* TODO: Add editing UI and impact analysis for this node */}
      </Drawer>
    </div>
  );
}